import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { KeyContactsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsInstitutionsSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as KeyContactActions from "../../../actions/keyContacts";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import { FormFilterInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { InstitutionLookupsLayout } from "../../index";

import "./KeyContactsPage.scss";

interface KeyContactsPageProps {
    formFilter: IFormFilter;
    keyContact: Dtos.KeyContact[];
    loadingKeyContacts: boolean;
    loadKeyContactsSuccess: boolean;
    loadKeyContactsFailure: boolean;
    loadKeyContactsErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    clearKeyContacts: KeyContactActions.IKeyContactClearActionCreator;
    loadKeyContacts: KeyContactActions.IKeyContactLoadActionCreator;
    filter: KeyContactActions.IKeyContactFilterActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
}

const KeyContactsPage: FC<KeyContactsPageProps> = ({
    formFilter,
    keyContact,
    loadingKeyContacts,
    loadKeyContactsSuccess,
    loadKeyContactsFailure,
    loadKeyContactsErrorCode,
    permissions,
    pageSize,
    clearKeyContacts,
    loadKeyContacts,
    filter,
    setBreadcrumbs,
    setDrawerItems,
    setPageSize
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showKeyContacts = !loadingKeyContacts && loadKeyContactsSuccess;


    // Load key contacts
    useEffect(() => {
        if (!initalised) {
            setDrawerItems(SettingsInstitutionsSubMenu(permissions));
            setBreadcrumbs(KeyContactsBreadcrumbs());

            loadKeyContacts();

            setInitalised(true);
        }
    }, [
        initalised,
        setDrawerItems,
        SettingsInstitutionsSubMenu,
        permissions,
        setBreadcrumbs,
        KeyContactsBreadcrumbs,
        loadKeyContacts,
        setInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadKeyContactsFailure) {
            navigate(`/error/${loadKeyContactsErrorCode}`);
        }
    }, [loadKeyContactsFailure, loadKeyContactsErrorCode, navigate]);

    // Cleanup effect to clear the key contacts on component unmount
    useEffect(() => {
        return () => {
            clearKeyContacts();
        };
    }, [clearKeyContacts]);

    const createTable = useMemo(() => {
        if (!keyContact || keyContact.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no key contacts to view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination = keyContact.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={keyContact}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : keyContact.length
                    }
                    pageSize={showPagination ? pageSize : keyContact.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no site locations to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (e, handleOriginal) => {
                                const url: string = `/settings/institution/keycontacts/${rowInfo.original.id}`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Light.LOOKUP}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "value",
                            Header: "Name",
                            accessor: "value",
                        },
                    ]}
                />
            </div>
        );
    }, [keyContact, pageSize, PAGE_SIZE_OPTIONS, setPageSize, navigate]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Key Contacts</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.KeyContactsCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={"/settings/institution/keycontacts/create"}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showKeyContacts ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showKeyContacts ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [formFilter, filter, permissions, showKeyContacts, createTable]);

    return (
        <InstitutionLookupsLayout
            loading={loadingKeyContacts}
            permission={Dtos.Permission.KeyContactsView}
        >
            {renderContent}
        </InstitutionLookupsLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<KeyContactsPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        formFilter: state.keyContacts.formFilter,
        keyContact: state.keyContacts.formFilteredData,
        loadingKeyContacts:
            state.keyContacts.loadState &&
            state.keyContacts.loadState.status == RequestState.Pending,
        loadKeyContactsSuccess:
            state.keyContacts.loadState &&
            state.keyContacts.loadState.status == RequestState.Success,
        loadKeyContactsFailure:
            state.keyContacts.loadState &&
            state.keyContacts.loadState.status == RequestState.Failure,
        loadKeyContactsErrorCode: state.keyContacts.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (dispatch): Partial<KeyContactsPageProps> => {
    return {
        loadKeyContacts: bindActionCreators(
            KeyContactActions.LoadKeyContact,
            dispatch
        ),
        clearKeyContacts: bindActionCreators(KeyContactActions.Clear, dispatch),
        filter: bindActionCreators(KeyContactActions.Filter, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(KeyContactsPage);
